/* ============================================================
   CHRONCES — <cr-welcome-popup>
   Pop-up de bienvenue : règles d'affichage + teaser + inscription
   Klaviyo par l'endpoint client `/client/subscriptions/`.
   Vanilla, aucune dépendance. Voir CLAUDE.md §27.
   ============================================================ */

const CR_WPOP_STORE = 'cr_welcome_popup';
// 🚨 Révision minimale : 2025-01-15. Vérifié en réel le 2026-08-02 contre
// l'API — en `2024-10-15` et `2024-02-15`, l'endpoint client répond
// « 'subscriptions' is not a valid field for the resource 'profile' » et
// AUCUNE inscription ne passe. Ne pas baisser cette valeur.
const CR_WPOP_REVISION = '2025-01-15';
const CR_WPOP_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

class CrWelcomePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cr-wpop-dialog]');
    this.form = this.querySelector('[data-cr-wpop-form]');
    this.teaser = this.querySelector('[data-cr-wpop-teaser]');
    if (!this.dialog) return;

    this.companyId = this.dataset.company || '';
    this.listEmail = this.dataset.listEmail || '';
    this.listSms = this.dataset.listSms || '';
    this.source = this.dataset.source || 'Popup Bienvenue';
    // 🚨 Ne PAS nommer cette propriété `prefix` : `Element.prototype.prefix`
    // existe déjà en lecture seule (préfixe de namespace XML). Un corps de
    // classe est en mode strict, donc `this.prefix = …` lève un TypeError qui
    // interrompt `connectedCallback` en silence — le pop-up ne s'ouvrait
    // jamais et la console restait vide. Même prudence pour tout nouveau nom.
    this.phonePrefix = this.dataset.prefix || '+33';
    this.country = this.dataset.country || '';
    this.phoneRequired = this.dataset.phoneRequired === 'true';
    this.delay = parseInt(this.dataset.delay || '6000', 10);
    this.reappearMs = parseInt(this.dataset.reappearDays || '7', 10) * 86400000;
    this.hideAfterSignup = this.dataset.hideAfterSignup === 'true';
    this.lockScroll = this.dataset.lockScroll === 'true';
    this.teaserEnabled = this.dataset.teaserEnabled === 'true';

    this.state = this.read();
    this.bind();

    // Dans l'éditeur, on n'ouvre jamais tout seul : le pop-up s'affiche
    // quand la section est sélectionnée dans la barre latérale. Sinon il
    // recouvrirait l'aperçu de chaque page qu'on essaie de régler.
    if (window.Shopify?.designMode) {
      this.bindDesignMode();
      return;
    }

    if (this.state.subscribed && this.hideAfterSignup) return;

    if (this.snoozed()) {
      this.showTeaser();
      return;
    }

    this.timer = setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.unlock();
  }

  /* ---------- Écouteurs ---------- */

  bind() {
    this.querySelectorAll('[data-cr-wpop-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.querySelector('[data-cr-wpop-open]')?.addEventListener('click', () => {
      this.hideTeaser({ remember: false });
      this.open();
    });
    this.querySelector('[data-cr-wpop-teaser-close]')?.addEventListener('click', () => {
      this.hideTeaser({ remember: true });
    });

    // Clic hors de la carte : la cible est le dialogue lui-même, puisque
    // la carte est un enfant centré qui capte ses propres clics.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    // 🚨 Le nettoyage NE DÉPEND PAS de l'événement `close` du <dialog>.
    // Vérifié en réel sur l'aperçu le 2026-08-02 : `close` n'est pas émis
    // dans ce navigateur, et le verrou de défilement restait posé — soit
    // exactement le bug qu'on reprochait au pop-up Klaviyo (§26c). Tout
    // passe donc par `onClosed()`, idempotent, appelé par `close()`.
    // L'écouteur reste comme filet pour une fermeture native.
    this.dialog.addEventListener('close', () => this.onClosed());

    // Échap : on annule la fermeture native pour repasser par notre chemin
    // unique, plutôt que d'espérer que l'événement qui suit soit émis.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    });

    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.querySelector('[data-cr-wpop-code]')?.addEventListener('click', (event) =>
      this.copyCode(event.currentTarget)
    );
  }

  bindDesignMode() {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close();
    });
  }

  /* ---------- Ouverture / fermeture ---------- */

  open() {
    if (this.dialog.open) return;
    this.closedHandled = false;
    this.dialog.showModal();
    this.lock();
    // Le focus part sur la croix plutôt que sur le champ e-mail : ouvrir
    // le clavier logiciel sans prévenir, sur mobile, est brutal.
    this.querySelector('[data-cr-wpop-close]')?.focus({ preventScroll: true });
  }

  close() {
    clearTimeout(this.timer);
    if (this.dialog.open) this.dialog.close();
    this.onClosed();
  }

  // Idempotent : appelé par `close()` ET par l'écouteur `close` du dialogue,
  // qui peuvent partir tous les deux selon le navigateur.
  onClosed() {
    if (this.closedHandled) return;
    this.closedHandled = true;
    this.unlock();
    if (this.subscribed) return;
    this.remember({ seen: Date.now() });
    this.showTeaser();
  }

  lock() {
    if (!this.lockScroll) return;
    // Largeur RÉELLE de la barre de défilement, mesurée pendant qu'elle est
    // encore là. 0 quand les barres sont superposées (macOS, iOS) : dans ce
    // cas masquer le défilement ne prend aucune largeur, donc on ne compense
    // rien — compenser quand même décalerait la page dans l'autre sens.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) {
      document.documentElement.style.setProperty('--cr-wpop-gap', `${gap}px`);
      document.documentElement.classList.add('cr-wpop-gutter');
    }
    document.documentElement.classList.add('cr-wpop-locked');
  }

  unlock() {
    document.documentElement.classList.remove('cr-wpop-locked', 'cr-wpop-gutter');
    document.documentElement.style.removeProperty('--cr-wpop-gap');
  }

  /* ---------- Teaser ---------- */

  showTeaser() {
    if (!this.teaserEnabled || !this.teaser) return;
    if (this.state.teaserClosed) return;
    if (this.state.subscribed && this.hideAfterSignup) return;
    this.teaser.hidden = false;
  }

  hideTeaser({ remember }) {
    if (this.teaser) this.teaser.hidden = true;
    if (remember) this.remember({ teaserClosed: true });
  }

  /* ---------- Mémoire ---------- */

  // localStorage lève dans certains navigateurs en navigation privée, et
  // le pop-up ne doit jamais faire tomber la page pour ça.
  read() {
    try {
      return JSON.parse(localStorage.getItem(CR_WPOP_STORE) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  remember(patch) {
    this.state = Object.assign({}, this.state, patch);
    try {
      localStorage.setItem(CR_WPOP_STORE, JSON.stringify(this.state));
    } catch (error) {
      /* stockage indisponible : on garde l'état en mémoire pour la session */
    }
  }

  snoozed() {
    if (!this.state.seen) return false;
    if (this.reappearMs === 0) return true; // 0 jour = ne plus jamais rouvrir seul
    return Date.now() - this.state.seen < this.reappearMs;
  }

  /* ---------- Téléphone ---------- */

  // Klaviyo exige du E.164. On accepte ce que les gens tapent vraiment
  // (« 06 12 34 56 78 », « 6.12.34.56.78 ») et on normalise.
  normalizePhone(raw) {
    const cleaned = String(raw).replace(/[^\d+]/g, '');
    if (!cleaned) return '';
    if (cleaned.startsWith('+')) return cleaned;
    return this.phonePrefix + cleaned.replace(/^0+/, '');
  }

  validPhone(value) {
    return /^\+[1-9]\d{7,14}$/.test(value);
  }

  /* ---------- Envoi ---------- */

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy) return;

    const emailInput = this.querySelector('[data-cr-wpop-email]');
    const phoneInput = this.querySelector('[data-cr-wpop-phone]');
    const email = (emailInput?.value || '').trim();
    const rawPhone = (phoneInput?.value || '').trim();

    this.setError('');
    emailInput?.setAttribute('aria-invalid', 'false');
    phoneInput?.setAttribute('aria-invalid', 'false');

    if (!emailInput?.checkValidity() || !email) {
      emailInput?.setAttribute('aria-invalid', 'true');
      emailInput?.focus();
      this.setError(this.dataset.errorEmail);
      return;
    }

    let phone = '';
    if (rawPhone) {
      phone = this.normalizePhone(rawPhone);
      if (!this.validPhone(phone)) {
        phoneInput.setAttribute('aria-invalid', 'true');
        phoneInput.focus();
        this.setError(this.dataset.errorPhone);
        return;
      }
    } else if (this.phoneRequired) {
      phoneInput?.setAttribute('aria-invalid', 'true');
      phoneInput?.focus();
      this.setError(this.dataset.errorPhone);
      return;
    }

    this.setBusy(true);
    try {
      await this.subscribe(email, phone);
      this.subscribed = true;
      this.remember({ subscribed: true, seen: Date.now(), teaserClosed: true });
      this.hideTeaser({ remember: false });
      this.showDone();
    } catch (error) {
      this.setError(this.dataset.errorGeneric);
    } finally {
      this.setBusy(false);
    }
  }

  async subscribe(email, phone) {
    if (!this.companyId || !this.listEmail) throw new Error('Klaviyo non configuré');

    const url = `${CR_WPOP_ENDPOINT}?company_id=${encodeURIComponent(this.companyId)}`;

    // Une requête = UNE liste. D'où deux appels quand un numéro est donné :
    // la liste e-mail et la liste SMS sont deux listes distinctes côté
    // Klaviyo, et le consentement SMS ne se donne qu'avec un numéro.
    const requests = [this.post(url, this.payload(this.listEmail, email, phone, ['email']))];
    if (phone && this.listSms) {
      requests.push(this.post(url, this.payload(this.listSms, email, phone, ['sms'])));
    }

    const results = await Promise.allSettled(requests);

    // Le succès se juge sur l'e-mail SEULEMENT. Un numéro refusé par
    // Klaviyo (indicatif inconnu, ligne fixe) ne doit pas faire croire au
    // client que son inscription a échoué : elle a bien eu lieu.
    if (results[0].status === 'rejected') throw results[0].reason;
    if (results[1]?.status === 'rejected') {
      console.warn('[cr-welcome-popup] inscription SMS refusée', results[1].reason);
    }

    this.identify(email);
  }

  // Rattache le profil à la session de navigation via le script onsite de
  // Klaviyo, déjà chargé sur la boutique. Deux effets : les événements du
  // site (Active on Site, Viewed Product) se rattachent au profil — donc les
  // flows de panier et de navigation abandonnés fonctionnent — et Klaviyo
  // renseigne la localisation depuis l'IP.
  // ⚠️ NE remplace PAS un correctif côté flow : l'inscription déclenche le
  // flow immédiatement, alors que la localisation n'arrive qu'après. Un flow
  // filtré sur le pays écartera quand même le profil (voir CLAUDE.md §27e).
  identify(email) {
    try {
      window.klaviyo?.identify?.({ $email: email });
    } catch (error) {
      /* script onsite absent ou bloqué : sans effet sur l'inscription */
    }
  }

  payload(listId, email, phone, channels) {
    const subscriptions = {};
    if (channels.includes('email')) subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    if (channels.includes('sms')) subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };

    const attributes = { email };
    if (phone) attributes.phone_number = phone;

    // 🚨 Sans localisation, le profil est ÉCARTÉ du flow de bienvenue :
    // `Welcome Series FR` filtre sur `location['country'] = "France"`, et
    // `/client/subscriptions/` ne géolocalise pas (voir CLAUDE.md §27e).
    // Le fuseau est celui du navigateur, donc vrai. Le pays vient d'un
    // réglage : c'est un CONTOURNEMENT du filtre, pas une mesure — le
    // correctif propre reste de retirer cette condition côté Klaviyo.
    const location = {};
    if (this.country) location.country = this.country;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) location.timezone = tz;
    } catch (error) {
      /* API absente : on se contente du pays */
    }
    if (Object.keys(location).length) attributes.location = location;

    attributes.subscriptions = subscriptions;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.source,
          profile: { data: { type: 'profile', attributes } },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    };
  }

  async post(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: CR_WPOP_REVISION },
      body: JSON.stringify(body),
    });
    // Vérifier `ok` AVANT de lire le corps : sur erreur, la réponse n'est
    // pas forcément du JSON, et un parse direct masquerait la cause.
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`Klaviyo ${response.status} ${detail}`.trim());
    }
    return response;
  }

  /* ---------- Rendu d'état ---------- */

  setBusy(busy) {
    this.busy = busy;
    const button = this.querySelector('[data-cr-wpop-submit]');
    if (!button) return;
    button.disabled = busy;
    if (busy) {
      button.dataset.label = button.textContent;
      button.textContent = this.dataset.submitBusy || '…';
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
  }

  setError(message) {
    const box = this.querySelector('[data-cr-wpop-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  showDone() {
    this.querySelector('[data-cr-wpop-pane="form"]').hidden = true;
    const done = this.querySelector('[data-cr-wpop-pane="done"]');
    done.hidden = false;
    done.querySelector('h2')?.focus?.();
  }

  async copyCode(button) {
    const code = button.dataset.code || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      const hint = button.parentElement.querySelector('[data-cr-wpop-code-hint]');
      if (!hint) return;
      const original = hint.textContent;
      hint.textContent = this.dataset.copied || 'Code copié';
      setTimeout(() => {
        hint.textContent = original;
      }, 2200);
    } catch (error) {
      /* presse-papiers refusé : le code reste lisible et sélectionnable */
    }
  }
}

if (!customElements.get('cr-welcome-popup')) {
  customElements.define('cr-welcome-popup', CrWelcomePopup);
}
